/**
 * Imperative API for the global modals stack. Works without React: call
 * `openModal()` from any event handler, async flow, or non-React module.
 *
 * Requires `<ModalsProvider>` mounted somewhere in the React tree (usually
 * the root). Without the provider these calls are no-ops at render time.
 */

import {
  modalsStore,
  type OpenModalPayload,
  type OpenConfirmModalPayload,
  type OpenContextModalPayload
} from './modals-store.js';

export function openModal(payload: OpenModalPayload): string {
  return modalsStore.open({ kind: 'modal', payload });
}

export function openConfirmModal(payload: OpenConfirmModalPayload): string {
  return modalsStore.open({ kind: 'confirm', payload });
}

export function openContextModal<TName extends string = string, TInner = unknown>(
  payload: OpenContextModalPayload<TName, TInner>
): string {
  return modalsStore.open({ kind: 'context', payload: payload as OpenContextModalPayload });
}

export function closeModal(id: string): void {
  modalsStore.close(id);
}

export function closeAllModals(): void {
  modalsStore.closeAll();
}

export function updateModal(id: string, patch: Partial<OpenModalPayload>): void {
  // Patch is type-loose at the store layer; consumers pass the right shape for the kind.
  modalsStore.update(id, { payload: patch } as unknown as Partial<{ kind: 'modal'; payload: OpenModalPayload }>);
}

/** Re-export the singleton for advanced inspection / DevTools integrations. */
export { modalsStore };
export type { OpenModalPayload, OpenConfirmModalPayload, OpenContextModalPayload };
